// src/components/features/content/form-steps/BasicInfoStepFallback.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Grid, Box, Typography, Chip, Autocomplete, TextField,
  FormControl, InputLabel, Select, MenuItem, FormHelperText
} from '@mui/material';
import { 
  Description as DescriptionIcon,
  Category as CategoryIcon,
  Business as BusinessIcon,
  Campaign as CampaignIcon,
  Language as LanguageIcon,
  Tag as TagIcon
} from '@mui/icons-material';

// Import content-specific components
import ContentTypeIcon from '../../../ui/ContentTypeIcon';
import { CONTENT_TYPES, LANGUAGE_OPTIONS } from '../form-utils/contentFormData';

// Import standardized form components
import { TextField as DesignTextField } from '../../../../design-system';
import { FormSection } from '../../../../design-system/components/forms';

const BasicInfoStepFallback = ({ 
  formik, 
  brands, 
  campaigns, 
  categoryOptions, 
  onContentTypeChange, 
  onBrandChange,
  isEditMode 
}) => {
  
  // Handle content type change
  const handleContentTypeChange = (e) => {
    const newType = e.target.value;
    formik.setFieldValue('type', newType);
    onContentTypeChange(newType);
  };
  
  // Handle hashtags input
  const handleHashtagsChange = (event, newHashtags) => {
    // Ensure hashtags don't have # symbol at the beginning
    const formattedHashtags = newHashtags.map(tag => 
      tag.startsWith('#') ? tag.substring(1) : tag
    );
    
    formik.setFieldValue('hashtags', formattedHashtags);
  };

  // Keep local state for dropdown selections to ensure UI updates correctly
  const [localBrandId, setLocalBrandId] = useState(formik.values.brandId || '');
  const [localCampaignId, setLocalCampaignId] = useState(formik.values.campaignId || '');
  const [selectedBrandName, setSelectedBrandName] = useState('');
  const [selectedCampaignName, setSelectedCampaignName] = useState('');
  const [forceUpdateFlag, setForceUpdateFlag] = useState(0); // Used to force re-renders
  const brandSelectRef = useRef(null);
  const campaignSelectRef = useRef(null);
  
  // Function to force a re-render
  const forceUpdate = () => {
    setForceUpdateFlag(prev => prev + 1);
  };
  
  // Sync local state with formik values on initial load and when values change externally
  useEffect(() => {
    setLocalBrandId(formik.values.brandId || '');
    
    // Update the selected brand name when brandId changes
    if (formik.values.brandId) {
      const selectedBrand = brands.find(b => b._id === formik.values.brandId);
      if (selectedBrand) {
        setSelectedBrandName(selectedBrand.name);
      }
    } else {
      setSelectedBrandName('');
    }
  }, [formik.values.brandId, brands]);
  
  // Sync campaign ID with formik values
  useEffect(() => {
    setLocalCampaignId(formik.values.campaignId || '');
    
    // Update the selected campaign name when campaignId changes
    if (formik.values.campaignId) {
      const selectedCampaign = campaigns.find(c => c._id === formik.values.campaignId);
      if (selectedCampaign) {
        setSelectedCampaignName(selectedCampaign.name);
      }
    } else {
      setSelectedCampaignName('');
    }
  }, [formik.values.campaignId, campaigns]);
  
  // Monitor props changes to force updates when campaigns or brands change
  useEffect(() => {
    // Force component re-render if campaigns change
    forceUpdate();
  }, [campaigns]);
  
  // Monitor formik values for any external changes
  useEffect(() => {
    // Force update when formik values change externally
    forceUpdate();
  }, [formik.values]);
  
  // Maintain state synchronization with minimal overhead

  return (
    <>

      {/* Title Section */}
      <FormSection
        title="Content Title"
        description="Give your content a clear and descriptive title."
        icon={<DescriptionIcon />}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <DesignTextField
              fullWidth
              id="title"
              name="title"
              label="Content Title"
              value={formik.values.title}
              onChange={formik.handleChange}
              error={formik.touched.title && Boolean(formik.errors.title)}
              helperText={formik.touched.title && formik.errors.title}
              required
            />
          </Grid>
        </Grid>
      </FormSection>

      {/* Content Type Section */}
      <FormSection
        title="Content Type & Category *"
        description="Specify what type of content you're creating. (required)"
        icon={<CategoryIcon />}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl 
              fullWidth 
              error={formik.touched.type && Boolean(formik.errors.type)}
            >
              <InputLabel id="type-label" required>Content Type *</InputLabel>
              <Select
                labelId="type-label"
                id="type"
                name="type"
                value={formik.values.type}
                onChange={handleContentTypeChange}
                label="Content Type"
                required
              >
                {CONTENT_TYPES.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ContentTypeIcon type={option.value} />
                      <Typography sx={{ ml: 1 }}>{option.label}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.type && formik.errors.type && (
                <FormHelperText>{formik.errors.type}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl 
              fullWidth
              error={formik.touched.category && Boolean(formik.errors.category)}
            >
              <InputLabel id="category-label" required>Category *</InputLabel>
              <Select
                labelId="category-label"
                id="category"
                name="category"
                value={formik.values.category}
                onChange={formik.handleChange}
                label="Category"
                required
              >
                {categoryOptions.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
              {formik.touched.category && formik.errors.category && (
                <FormHelperText>{formik.errors.category}</FormHelperText>
              )}
            </FormControl>
          </Grid>
        </Grid>
      </FormSection>

      {/* Brand & Campaign Section */}
      <FormSection
        title="Brand & Campaign *"
        description="Associate this content with a brand and optional campaign. (Brand is required)"
        icon={<BusinessIcon />}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl 
              fullWidth 
              error={formik.touched.brandId && Boolean(formik.errors.brandId)}
            >
              <InputLabel id="brandId-label" required>Brand *</InputLabel>
              <Select
                labelId="brandId-label"
                id="brandId"
                name="brandId"
                value={localBrandId}
                inputRef={brandSelectRef}
                onChange={(e) => {
                  const value = e.target.value;
                  
                  // Update local state immediately to ensure UI updates
                  setLocalBrandId(value);
                  
                  // Find selected brand name
                  const selectedBrand = brands.find(b => b._id === value);
                  if (selectedBrand) {
                    setSelectedBrandName(selectedBrand.name);
                  } else {
                    setSelectedBrandName('');
                  }
                  
                  // Set brandId using formik
                  formik.setFieldValue('brandId', value);
                  
                  // Clear campaignId
                  formik.setFieldValue('campaignId', '');
                  
                  // Call onBrandChange callback
                  if (onBrandChange) {
                    onBrandChange(value);
                  }
                  
                  // Force a component re-render after a short delay
                  setTimeout(() => forceUpdate(), 100);
                }}
                label="Brand"
                required
                disabled={isEditMode}
              >
                {brands.map(brand => (
                  <MenuItem key={brand._id} value={brand._id}>
                    {brand.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {formik.touched.brandId && formik.errors.brandId ? 
                  formik.errors.brandId : 
                  isEditMode ? 'Brand cannot be changed after creation' : ''}
              </FormHelperText>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="campaignId-label">Campaign (Optional)</InputLabel>
              <Select
                labelId="campaignId-label"
                id="campaignId"
                name="campaignId"
                value={localCampaignId}
                inputRef={campaignSelectRef}
                onChange={(e) => {
                  const value = e.target.value;
                  
                  // Update local state immediately to ensure UI updates
                  setLocalCampaignId(value);
                  
                  // Find selected campaign name
                  const selectedCampaign = campaigns.find(c => c._id === value);
                  if (selectedCampaign) {
                    setSelectedCampaignName(selectedCampaign.name);
                  } else {
                    setSelectedCampaignName('');
                  }
                  
                  // Set campaignId using formik
                  formik.setFieldValue('campaignId', value);
                  
                  // Force a component re-render after a short delay
                  setTimeout(() => {
                    forceUpdate();
                  }, 100);
                }}
                label="Campaign (Optional)"
                disabled={!formik.values.brandId}
              >
                <MenuItem value="">None</MenuItem>
                {campaigns.length === 0 ? (
                  <MenuItem disabled>No campaigns available for this brand</MenuItem>
                ) : (
                  campaigns.map(campaign => (
                    <MenuItem key={campaign._id} value={campaign._id}>
                      {campaign.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </FormSection>

      {/* Content Details Section */}
      <FormSection
        title="Content Details"
        description="Add the main text for your content."
        icon={<DescriptionIcon />}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="language-label">Language</InputLabel>
              <Select
                labelId="language-label"
                id="language"
                name="language"
                value={formik.values.language}
                onChange={formik.handleChange}
                label="Language"
              >
                {LANGUAGE_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <DesignTextField
              fullWidth
              multiline
              rows={6}
              id="text"
              name="text"
              label="Content Text"
              value={formik.values.text}
              onChange={formik.handleChange}
              error={formik.touched.text && Boolean(formik.errors.text)}
              helperText={
                (formik.touched.text && formik.errors.text) || 
                `${formik.values.text?.length || 0}/2200 characters`
              }
              placeholder="Enter your content text here..."
            />
          </Grid>
        </Grid>
      </FormSection>

      {/* Hashtags Section */}
      <FormSection
        title="Hashtags"
        description="Add hashtags for your content"
        icon={<TagIcon />}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              freeSolo
              id="hashtags"
              options={[]}
              value={formik.values.hashtags || []}
              onChange={handleHashtagsChange}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip 
                    label={`#${option}`} 
                    {...getTagProps({ index })} 
                    key={index}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Hashtags"
                  placeholder="Add hashtags and press Enter"
                  helperText="Add hashtags without the # symbol"
                />
              )}
            />
          </Grid>
        </Grid>
      </FormSection>
    </>
  );
};

export default BasicInfoStepFallback;